import { useEffect, useMemo, useState } from "react";
import { Dices, Filter, Upload } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card.js";
import { MultiSelect } from "../../../components/ui/multi-select.js";
import { Input } from "../../../components/ui/input.js";
import { Label } from "../../../components/ui/label.js";
import { DateInput } from "../../../components/ui/date-input.js";
import { SelectField } from "../../../components/ui/select-field.js";
import { Checkbox } from "../../../components/ui/checkbox.js";
import { RangeSlider } from "../../../components/ui/range-slider.js";
import { FileInput } from "../../../components/ui/file-input.js";
import { Button } from "../../../components/ui/button.js";
import { Badge } from "../../../components/ui/badge.js";
import { clean, distinctValues, splitCodes, type SelectionFilters, type SelectionOrder, type ValueCounts } from "../../../lib/migration-sim/selection.js";
import type { SimEmployee } from "../../../lib/migration-sim/source.js";
import { DRAFT_SCRIPTS, type Draft } from "../../../lib/migration-sim/draft.js";

const SCRIPT_LABELS: Record<Draft["script"], string> = {
  preview: "preview: list who is picked, no writes",
  "migrate:team": "migrate:team: load the picked employees",
  "migrate:all": "migrate:all: team, then modules, verify, status",
};

interface Props {
  draft: Draft;
  onChange: (d: Draft) => void;
  source: SimEmployee[];
  migrated: Set<number>;
  files: Record<string, string>;
  onUploadCodes: (name: string, text: string) => void;
  /** Live result of the current filters: null while they don't validate. */
  preview: { matched: number; already: number; picked: number; managers: number; unknown: string[] } | null;
  previewError: string | null;
  disabled: boolean;
}

/** The interactive picker's questions as a form; every change rewrites the command box. */
export function FilterPanel({ draft, onChange, source, migrated, files, onUploadCodes, preview, previewError, disabled }: Props) {
  const f = draft.filters;
  // Counts are people not yet migrated, like the tool's interactive picker.
  const values = useMemo(() => {
    const remaining = source.filter((m) => !migrated.has(m.legacyBayUserId));
    return distinctValues(remaining.length ? remaining : source);
  }, [source, migrated]);

  const setF = (patch: Partial<SelectionFilters>) => onChange({ ...draft, filters: clean({ ...f, ...patch }) });
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });

  const multi = (label: string, key: "statuses" | "departments" | "roles" | "offices" | "types" | "locations", counts: ValueCounts) => (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <MultiSelect
        options={counts.map(([v, n]) => ({ value: v, label: `${v} (${n})` }))}
        selected={new Set(f[key] ?? [])}
        onSelectedChange={(next) => setF({ [key]: next.size ? [...next] : undefined })}
        placeholder="Any"
        disabled={disabled}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Filter className="h-4 w-4" /> Choose who to migrate</CardTitle>
        <CardDescription>Filters combine with AND; several values in one filter mean OR. People already migrated are left out unless you re-load them.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label>Command</Label>
          <SelectField value={draft.script} onValueChange={(v) => set({ script: v as Draft["script"] })} disabled={disabled}>
            {DRAFT_SCRIPTS.map((s) => <option key={s} value={s}>{SCRIPT_LABELS[s]}</option>)}
          </SelectField>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {multi("Employee status", "statuses", values.statuses)}
          {multi("Department", "departments", values.departments)}
          {multi("Role", "roles", values.roles)}
          {multi("Office", "offices", values.offices)}
          {multi("Employment type", "types", values.types)}
          {multi("Work location", "locations", values.locations)}
          <div className="flex flex-col gap-1">
            <Label>Hired on or after</Label>
            <DateInput value={f.hiredFrom ?? ""} onChange={(v) => setF({ hiredFrom: v || undefined })} disabled={disabled} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Hired on or before</Label>
            <DateInput value={f.hiredTo ?? ""} onChange={(v) => setF({ hiredTo: v || undefined })} disabled={disabled} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Designation contains</Label>
            <CsvInput placeholder="e.g. engineer, recruiter" value={f.designations} onChange={(v) => setF({ designations: v })} disabled={disabled} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Email domain</Label>
            <CsvInput placeholder="e.g. integertel.com" value={f.emailDomains} onChange={(v) => setF({ emailDomains: v?.map((d) => d.toLowerCase().replace(/^@/, "")) })} disabled={disabled} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Employee codes</Label>
            <CsvInput placeholder="IT001, IT014 (old or new code)" value={f.codes} onChange={(v) => setF({ codes: v })} disabled={disabled} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Legacy ids (bay_users.id)</Label>
            <CsvInput placeholder="e.g. 104, 131" value={f.legacyIds?.map(String)} onChange={(v) => { const ids = v?.map(Number).filter(Number.isInteger); setF({ legacyIds: ids?.length ? ids : undefined }); }} disabled={disabled} />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Codes file (.txt / .csv)</Label>
          <div className="flex flex-wrap items-center gap-2">
            <FileInput
              accept=".txt,.csv"
              placeholder={f.codesFile ? `Replace ${f.codesFile}` : "Choose a codes file"}
              disabled={disabled}
              onChange={async (file) => {
                if (!file) return;
                onUploadCodes(file.name, await file.text());
                setF({ codesFile: file.name });
              }}
            />
            {f.codesFile && (
              <>
                <Badge tone="info">{f.codesFile}: {splitCodes(files[f.codesFile] ?? "").length} codes</Badge>
                <Button variant="ghost" size="xs" onClick={() => setF({ codesFile: undefined })} disabled={disabled}>Remove</Button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-surface-200 p-3">
          <div className="flex items-center justify-between">
            <Label>Percent of the matching people</Label>
            <span className="text-sm font-semibold text-surface-900">{f.percent ?? 100}%</span>
          </div>
          <RangeSlider min={1} max={100} value={f.percent ?? 100} onValueChange={(v) => setF({ percent: v === 100 ? undefined : v })} disabled={disabled} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <Label>At most (limit)</Label>
              <Input type="number" min={1} placeholder="No cap" value={f.limit ?? ""} onChange={(e) => setF({ limit: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })} disabled={disabled} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Take first</Label>
              <SelectField
                value={f.order ?? "id"}
                onValueChange={(v) => setF({ order: v === "id" ? undefined : (v as SelectionOrder), seed: v === "random" ? f.seed ?? Math.floor(Math.random() * 2 ** 31) : undefined })}
                disabled={disabled}
              >
                <option value="id">Legacy id order</option>
                <option value="hire-date">Earliest hired</option>
                <option value="random">Random (seeded)</option>
              </SelectField>
            </div>
            <div className="flex flex-col gap-1">
              <Label>Seed</Label>
              <div className="flex gap-1">
                <Input type="number" value={f.seed ?? ""} placeholder="—" onChange={(e) => setF({ seed: e.target.value ? Number(e.target.value) : undefined })} disabled={disabled || f.order !== "random"} />
                <Button variant="secondary" size="sm" aria-label="New random seed" title="New random seed" onClick={() => setF({ seed: Math.floor(Math.random() * 2 ** 31) })} disabled={disabled || f.order !== "random"}>
                  <Dices className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Toggle label="Also migrate their managers" hint="--with-managers" checked={!!f.withManagers} onChange={(v) => setF({ withManagers: v || undefined })} disabled={disabled} />
          <Toggle label="Re-load people already migrated" hint="--include-migrated" checked={!!f.includeMigrated} onChange={(v) => setF({ includeMigrated: v || undefined })} disabled={disabled} />
          <Toggle label="Dry run (no writes, nobody remembered)" hint="--dry-run" checked={draft.dryRun} onChange={(v) => set({ dryRun: v })} disabled={disabled} />
          <Toggle label="Use HR's Active Employee Report" hint="--hr-report / --no-hr-report" checked={draft.hrReport} onChange={(v) => set({ hrReport: v })} disabled={disabled || draft.script === "preview"} />
          <Toggle label="Load legacy documents" hint="--docs-dir" checked={draft.docs} onChange={(v) => set({ docs: v })} disabled={disabled || draft.script === "preview"} />
          <Toggle label="Truncate HRMS first (destructive)" hint="--reset" checked={draft.reset} onChange={(v) => set({ reset: v })} disabled={disabled || draft.script === "preview"} />
        </div>

        <div className="rounded-lg bg-surface-50 p-3 text-sm">
          {previewError ? (
            <p className="whitespace-pre-wrap text-[var(--color-error-fg)]">{previewError}</p>
          ) : preview ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">{preview.matched} match</Badge>
              <Badge tone="warning">{preview.already} already migrated</Badge>
              <Badge tone="success">{preview.picked} would be picked{preview.managers ? ` (incl. ${preview.managers} managers)` : ""}</Badge>
              {preview.unknown.length > 0 && <Badge tone="error">codes not found: {preview.unknown.join(", ")}</Badge>}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function Toggle({ label, hint, checked, onChange, disabled }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void; disabled: boolean }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-surface-200 p-2 text-sm has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} disabled={disabled} className="mt-0.5" />
      <span className="flex flex-col">
        <span className="font-medium text-surface-800">{label}</span>
        <span className="font-mono text-[11px] text-surface-500">{hint}</span>
      </span>
    </label>
  );
}

const parseCsv = (s: string) => { const v = s.split(",").map((x) => x.trim()).filter(Boolean); return v.length ? v : undefined; };

// A comma-separated list field that keeps what is being typed ("IT001, ") while the filters
// only ever hold the parsed list, and follows the command box when that changes the list.
function CsvInput({ value, onChange, placeholder, disabled }: { value: string[] | undefined; onChange: (v: string[] | undefined) => void; placeholder: string; disabled: boolean }) {
  const joined = (value ?? []).join(", ");
  const [text, setText] = useState(joined);
  useEffect(() => {
    setText((t) => ((parseCsv(t) ?? []).join(", ") === joined ? t : joined));
  }, [joined]);
  return <Input placeholder={placeholder} value={text} onChange={(e) => { setText(e.target.value); onChange(parseCsv(e.target.value)); }} disabled={disabled} />;
}
